/* "Needs attention": the gaps in the selected dates, worst first, each with a one-click way to the shipments behind it.
   Built from the summary only (no extra call). Severity is shown as text + icon, never colour alone. */
import React, { useMemo, useState } from 'react'
import { Box, Button, Card, CardContent, Chip, Typography } from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { TONE, fmtHours, num, pct } from './podUtils'

const LEVEL = {
  critical: { label: 'Critical', tone: 'error', Icon: ErrorOutlineIcon },
  warning: { label: 'Below target', tone: 'orange', Icon: WarningAmberOutlinedIcon },
  info: { label: 'Worth a look', tone: 'neutral', Icon: InfoOutlinedIcon },
}
const ORDER = { critical: 0, warning: 1, info: 2 }
const SHOW = 4

const days = (n) => `${n} ${n === 1 ? 'day' : 'days'}`

/** The list of findings for a summary. Each: { key, level, title, detail, action: { label, run } }. */
export function buildInsights(summary, actions) {
  if (!summary?.totals) return []
  const t = summary.totals
  const labels = summary.bucketLabels || []
  const last = labels.length - 1
  const recT = summary.receivedTargetPct
  const otT = summary.onTimeTargetPct
  const out = []

  const old = t.pendingByBucket?.[last] || 0
  if (old > 0) {
    out.push({
      key: 'old', level: 'critical',
      title: `${num(old)} delivered ${old === 1 ? 'shipment has' : 'shipments have'} had no PoD for ${labels[last]?.toLowerCase()}`,
      detail: `Oldest is ${days(t.oldestPendingDays)} past delivery. Chase the courier or upload the PoD manually.`,
      action: { label: 'List them', run: () => actions.showRows({ podStatus: 'PENDING', agingBucket: last }) },
    })
  }

  const below = (rows) => (rows || []).filter((r) => r.key && r.delivered > 0 && r.receivedPct != null && r.receivedPct < recT)
    .sort((a, b) => b.pending - a.pending || a.receivedPct - b.receivedPct)
  below(summary.byCourier).slice(0, 3).forEach((r) => out.push({
    key: `c-${r.key}`, level: (r.pendingByBucket?.[last] || 0) > 0 ? 'critical' : 'warning',
    title: `${r.name}: ${pct(r.receivedPct)} PoDs received (target ${recT}%)`,
    detail: `${num(r.pending)} pending${r.oldestPendingDays != null ? `, oldest ${days(r.oldestPendingDays)}` : ''} · PoD via ${r.podVia === 'API' ? 'API' : 'manual upload'}.`,
    action: { label: 'Show courier', run: () => actions.focus({ courier: r.key }) },
  }))
  below(summary.byClient).slice(0, 3).forEach((r) => out.push({
    key: `m-${r.key}`, level: 'warning',
    title: `${r.name}: ${pct(r.receivedPct)} PoDs received (target ${recT}%)`,
    detail: `${num(r.pending)} pending${r.oldestPendingDays != null ? `, oldest ${days(r.oldestPendingDays)}` : ''}. This client is not getting all its PoDs.`,
    action: { label: 'Show client', run: () => actions.focus({ client: r.key }) },
  }))

  ;(summary.byCourier || []).filter((r) => r.key && r.late > 0 && r.onTimePct != null && r.onTimePct < otT)
    .sort((a, b) => b.late - a.late).slice(0, 2).forEach((r) => out.push({
      key: `l-${r.key}`, level: 'warning',
      title: `${r.name}: ${num(r.late)} PoDs uploaded late, ${pct(r.onTimePct)} on time (target ${otT}%)`,
      detail: `Average ${fmtHours(r.avgUploadHours)} from delivery to PoD; the window is ${summary.onTimeHours} h.`,
      action: { label: 'See late ones', run: () => actions.focusRows({ courier: r.key }, { podStatus: 'RECEIVED', timeliness: 'LATE' }) },
    }))

  const api = (summary.bySource || []).find((s) => s.key === 'API')
  const man = (summary.bySource || []).find((s) => s.key === 'MANUAL')
  if (api?.avgUploadHours != null && man?.avgUploadHours != null && man.avgUploadHours > api.avgUploadHours * 1.5) {
    out.push({
      key: 'src', level: 'info',
      title: `Manual PoDs take ${fmtHours(man.avgUploadHours)} on average, API PoDs ${fmtHours(api.avgUploadHours)}`,
      detail: `${num(man.received)} PoDs came in manually. Moving those couriers to API would cut the wait.`,
      action: { label: 'See manual ones', run: () => actions.showRows({ podStatus: 'RECEIVED', podSource: 'MANUAL' }) },
    })
  }
  return out.sort((a, b) => ORDER[a.level] - ORDER[b.level])
}

export default function PodInsights({ summary, loading, actions }) {
  const [all, setAll] = useState(false)
  const items = useMemo(() => buildInsights(summary, actions), [summary, actions])
  const shown = all ? items : items.slice(0, SHOW)
  const critical = items.filter((i) => i.level === 'critical').length

  return (
    <Card elevation={0} sx={{ border: 1, borderColor: 'divider', height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography component='h2' sx={{ fontWeight: 700, fontSize: 16 }}>Needs attention</Typography>
          {items.length > 0 && <Chip size='small' label={`${items.length} ${items.length === 1 ? 'issue' : 'issues'}${critical ? ` · ${critical} critical` : ''}`}
            sx={{ bgcolor: TONE[critical ? 'error' : 'orange'].bg, color: TONE[critical ? 'error' : 'orange'].fg, fontWeight: 600, '& .MuiChip-label': { textTransform: 'none' } }} />}
          <Typography sx={{ fontSize: 12, color: 'text.secondary', ml: { sm: 'auto' } }}>
            Worst first, against your targets
          </Typography>
        </Box>

        {!loading && summary && items.length === 0 && (
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', p: 1.5, borderRadius: 2, bgcolor: TONE.success.bg, color: TONE.success.fg }}>
            <CheckCircleOutlineIcon fontSize='small' aria-hidden />
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>No gaps: every courier and client meets the targets for these dates.</Typography>
          </Box>
        )}

        <Box component='ul' sx={{ listStyle: 'none', m: 0, p: 0, border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          {shown.map((i) => {
            const L = LEVEL[i.level]
            const t = TONE[L.tone]
            return (
              <Box component='li' key={i.key} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 1, px: 1.5, borderLeft: 3, borderLeftColor: t.fg,
                '&:not(:last-of-type)': { borderBottom: 1, borderBottomColor: 'divider' }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                <L.Icon fontSize='small' sx={{ color: t.fg, flexShrink: 0 }} aria-hidden />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
                    <Box component='span' sx={{ color: t.fg, fontWeight: 700, mr: 1, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.03em' }}>{L.label}</Box>
                    {i.title}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{i.detail}</Typography>
                </Box>
                <Button size='small' onClick={i.action.run} sx={{ textTransform: 'none', whiteSpace: 'nowrap', minHeight: 36, flexShrink: 0 }}>
                  {i.action.label}
                </Button>
              </Box>
            )
          })}
        </Box>
        {items.length > SHOW && (
          <Button size='small' onClick={() => setAll((v) => !v)} sx={{ textTransform: 'none', mt: 1 }}>
            {all ? 'Show fewer' : `Show all ${items.length}`}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
